import type { ApplicantSectionV2 } from "@credit-acceptance/step6-bff-client";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "../../hooks/useAuth";
import type { WorkbenchPanelPermissions } from "../../lib/permissions/resolvePanelPermissions";
import { useActionPanelStore } from "../../stores/actionPanelStore";
import { useAnalystNotes } from "../analystNotes/api/useAnalystNotes";
import { ReadOnlyProvider, useReadOnly } from "../lock/ReadOnlyContext";
import { hasAnalystNotesAction, selectPopoverActions } from "./actionRegistry";
import ActionsPopover from "./ActionsPopover";
import AnalystNotesModal from "./AnalystNotesModal";
import { useWorkbenchActions } from "./api/useWorkbenchActions";
import InternalAuditDrawer from "./InternalAuditDrawer";

interface ActionFabClusterProperties {
  /** The deal / approval whose data the Internal / Audit drawer shows. */
  approvalId: string;
  /** Driver applicant section from the dashboard response, forwarded to the Actions popover's View Bureau modal. */
  driver: ApplicantSectionV2 | undefined;
  /** 2nd signer applicant section, forwarded to the Actions popover's View Bureau modal. */
  secondSigner: ApplicantSectionV2 | undefined;
  /** Dealer id from the dashboard's deal summary, forwarded to the View Bureau repull. */
  dealerId: string | undefined;
  internalVisible?: boolean;
  canViewPreApprovalHistory?: boolean;
  access: WorkbenchPanelPermissions["actions"];
}

/**
 * Fixed-position cluster of up to three floating action buttons anchored to the
 * bottom-right of the viewport. Enforces a single-open-panel invariant:
 * opening any panel automatically closes the others.
 *
 * Built as a self-contained feature — can be hoisted from ReviewPage to a
 * global shell with zero changes to this component.
 *
 * z-index: 40 sits above the page grid and footer (z-10 / z-20) but below
 * Radix portals (z-50) so that the popover/drawer overlays render on top.
 */
function ActionFabCluster({
  approvalId,
  driver,
  secondSigner,
  dealerId,
  internalVisible = false,
  canViewPreApprovalHistory = false,
  access,
}: ActionFabClusterProperties) {
  const { t } = useTranslation();
  const readOnly = useReadOnly();
  const openPanel = useActionPanelStore((state) => state.openPanel);
  const setOpenPanel = useActionPanelStore((state) => state.setOpenPanel);
  const auditTabId = useActionPanelStore((state) => state.auditTabId);

  const handleOpenChange = (panel: NonNullable<typeof openPanel>) => (shouldOpen: boolean) => {
    setOpenPanel(shouldOpen ? panel : undefined);
  };

  // Auto-open the Analyst Notes modal on first load when the deal already has
  // notes (caps2 parity). The ref latches on the FIRST settled fetch regardless
  // of count, so a re-render, a panel switch, or a later save that repopulates
  // the list (e.g. adding the first note to a previously-empty deal) never
  // re-triggers the auto-open — it fires at most once per mount.
  const { user, isLoading: isAuthLoading } = useAuth();
  const roleName = user?.userRole ?? "";
  const actionsQuery = useWorkbenchActions(approvalId, roleName);
  // The query is disabled until the role resolves, and a disabled query reports
  // `isLoading: false` with `data: undefined` — rendering that straight through
  // would flash (and, if the role never resolves, permanently show) the
  // terminal "No actions available" message instead of the loading state.
  const isActionsLoading = actionsQuery.isPending && (!!roleName || isAuthLoading);
  const hasAnalystNotes = hasAnalystNotesAction(actionsQuery.data);
  const popoverActions = access.analystNotesOnly ? [] : selectPopoverActions(actionsQuery.data);
  // On a failed fetch we can't tell whether the role may enter analyst notes.
  // Keep the FAB visible but disabled rather than silently removing it — a
  // missing FAB is indistinguishable from "your role doesn't have this".
  const showAnalystNotes = hasAnalystNotes || actionsQuery.isError;

  const { data: analystNotes } = useAnalystNotes(approvalId);
  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (!hasAnalystNotes || hasAutoOpened.current || analystNotes === undefined) {
      return;
    }
    hasAutoOpened.current = true;
    if (analystNotes.length > 0) {
      setOpenPanel("analyst-notes");
    }
  }, [hasAnalystNotes, analystNotes, setOpenPanel]);

  return (
    /* The cluster itself is z-40; Radix portals render at z-50 automatically.
       bottom reads --fab-offset (see theme.css) — the single source of truth
       shared with TwoColumnLayout's aside padding, so the two can't drift
       apart again. */
    <div
      role="region"
      aria-label={t("actions.quickActions")}
      className="fixed bottom-[var(--fab-offset)] right-6 z-40 flex items-center gap-3 md:right-8"
    >
      {showAnalystNotes && (
        <ReadOnlyProvider value={readOnly || !access.canEditAnalystNotes || !hasAnalystNotes || actionsQuery.isError}>
          <AnalystNotesModal
            open={openPanel === "analyst-notes"}
            onOpenChange={handleOpenChange("analyst-notes")}
            approvalId={approvalId}
            disabled={!hasAnalystNotes}
            disabledLabel={t("actions.analystNotesUnavailable")}
          />
        </ReadOnlyProvider>
      )}
      {internalVisible && (
        <InternalAuditDrawer
          open={openPanel === "internal-audit"}
          onOpenChange={handleOpenChange("internal-audit")}
          approvalId={approvalId}
          initialTabId={auditTabId}
          canViewPreApprovalHistory={canViewPreApprovalHistory}
        />
      )}
      <ReadOnlyProvider value={readOnly || !access.canRunActions}>
        <ActionsPopover
          open={openPanel === "actions"}
          onOpenChange={handleOpenChange("actions")}
          approvalId={approvalId}
          driver={driver}
          secondSigner={secondSigner}
          dealerId={dealerId}
          actions={popoverActions}
          isLoading={isActionsLoading}
          isError={actionsQuery.isError}
          onRetry={() => void actionsQuery.refetch()}
        />
      </ReadOnlyProvider>
    </div>
  );
}

export default ActionFabCluster;
